import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

export interface Notificacao {
  id: number;
  tipo: string;
  titulo: string;
  mensagem: string;
  dados_extras?: Record<string, unknown>;
  criado_em: string;
  lida?: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const BACKEND_URL = API_URL.replace(/\/api\/?$/, '') || window.location.origin;

export function useNotificacoes() {
  const { usuario, token } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);

  const carregarInicial = useCallback(async () => {
    if (!usuario) return;
    try {
      const { data } = await api.get<Notificacao[]>('/notificacoes');
      setNotificacoes(data);
      setTotalNaoLidas(data.filter((n) => !n.lida).length);
    } catch {
      // silencia erros de rede — não é crítico
    }
  }, [usuario]);

  useEffect(() => {
    if (!usuario || !token) return;

    carregarInicial();

    const socket: Socket = io(`${BACKEND_URL}/notificacoes`, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('autenticar', {
        id_usuario: usuario.id,
        id_creche: usuario.id_creche,
        token,
      });
    });

    socket.on('notificacao', (nova: Notificacao) => {
      setNotificacoes((prev) => [nova, ...prev].slice(0, 100));
      setTotalNaoLidas((prev) => prev + 1);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [usuario, token, carregarInicial]);

  const marcarComoLida = useCallback(async (id: number) => {
    try {
      await api.put(`/notificacoes/${id}/lida`);
      setNotificacoes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lida: true } : n)),
      );
      setTotalNaoLidas((prev) => Math.max(0, prev - 1));
    } catch {
      // silencia
    }
  }, []);

  const marcarTodasComoLidas = useCallback(async () => {
    try {
      await api.put('/notificacoes/marcar-todas-lidas');
      setNotificacoes((prev) => prev.map((n) => ({ ...n, lida: true })));
      setTotalNaoLidas(0);
    } catch {
      // silencia
    }
  }, []);

  return { notificacoes, totalNaoLidas, marcarComoLida, marcarTodasComoLidas };
}
